import 'react-datepicker/dist/react-datepicker.css';

import { Box, IconButton, Text } from '@youversion/design-system-components';
import Spinner from '@youversion/design-system-icons/lib/Spinner';
import XStroked from '@youversion/design-system-icons/lib/XStroked';
import Modal from 'components/ui/modal';
import ModalFooter from 'components/ui/modal/Footer';
import ModalHeader from 'components/ui/modal/Header';
import { add, endOfMonth, format, isSameDay, setDate } from 'date-fns';
import { useSelectorEvents } from 'hooks/useSelectorEvents';
import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import React, { useEffect } from 'react';
import { firebaseLogEvent } from 'utils/firebase';
import { useFrequency } from 'utils/store/frequency';
import { usePaymentDate } from 'utils/store/paymentDate';

const DatePicker = dynamic(() => import('react-datepicker'), {
  loading: () => <Spinner dur={0.9} />,
});

interface Props {
  isScheduledGift?: boolean;
  onClose: () => void;
}

export default function ProcessDateDialog({ isScheduledGift = false, onClose }: Props) {
  const { selectedPaymentDate, setPaymentDate } = usePaymentDate();
  const { selectedFrequency } = useFrequency();
  const { t } = useTranslation();
  const { logSelectorDismissedEvent, logSelectorPresentedEvent } = useSelectorEvents('giving_process_date');

  const tomorrow = add(new Date(), { days: 1 });

  function getProcessDateLabel(selectedDate: Date) {
    switch (true) {
      case isSameDay(new Date(), selectedDate):
        return t('giving.labels.process_date_today', { date: format(new Date(), 'PP') });
      case isSameDay(tomorrow, selectedDate):
        return t('giving.labels.process_date_tomorrow', { date: format(tomorrow, 'PP') });
      default:
        return t('giving.labels.process_date_date', { date: format(selectedDate, 'PP') });
    }
  }

  const processDateText = getProcessDateLabel(selectedPaymentDate);

  useEffect(() => {
    firebaseLogEvent('GivingScreenView', { screen: 'giving_process_date' });
    logSelectorPresentedEvent(format(selectedPaymentDate, 'yyyy-LL-dd'));
    return () => void logSelectorDismissedEvent(format(selectedPaymentDate, 'yyyy-LL-dd'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal onClose={onClose}>
      <div className='flex flex-col justify-between h-full'>
        <ModalHeader
          left={
            <IconButton className='inline-end-2 block-start-2' data-testid='close-modal-button' onClick={onClose}>
              <XStroked className='text-text-light dark:text-text-dark' />
              <label className='sr-only'>{t('giving.cta.close')}</label>
            </IconButton>
          }
          title={t('giving.titles.process_date')}
        />
        <Box className='grow overflow-x-auto pli-3'>
          <Box className='datepicker'>
            <DatePicker
              excludeDateIntervals={
                selectedFrequency === 'TWICE_MONTHLY'
                  ? [
                      { end: setDate(new Date(), 14), start: setDate(new Date(), 1) },
                      { end: endOfMonth(new Date()), start: setDate(new Date(), 15) },
                    ]
                  : []
              }
              excludeDates={isScheduledGift ? [new Date()] : []}
              inline={true}
              minDate={new Date()}
              onChange={(date: Date) => {
                void setPaymentDate(date);
                firebaseLogEvent('GivingButtonTap', {
                  label: 'Process Date',
                  screen: 'giving_process_date',
                  value: format(date, 'yyyy-LL-dd'),
                });
              }}
              selected={selectedPaymentDate}
              useWeekdaysShort={true}
            />
          </Box>
          <Text className='text-center' size='md' variant='caption'>
            {processDateText}
          </Text>
        </Box>
        <ModalFooter
          buttonText={t('giving.cta.done')}
          onClick={() => {
            firebaseLogEvent('GivingButtonTap', { label: 'Done', screen: 'giving_process_date' });
            onClose();
          }}
        />
      </div>
    </Modal>
  );
}
